import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../services/api';
import './ParentSections.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ParentSectionChildren = () => {
  const { t } = useTranslation(['parent', 'common']);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [newChild, setNewChild] = useState({
    name: '',
    age: '',
    grade: '',
    email: '',
    password: ''
  });

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const parentId = user.id;

  // Fetch children from API
  useEffect(() => {
    const fetchChildren = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setChildren(data.data);
          }
        } else {
          // Fallback to mock data if API fails
          console.warn('Failed to fetch children, using mock data');
          setChildren([
            {
              id: 1,
              name: 'Emma Smith',
              age: 12,
              grade: '6th Grade',
              email: 'emma.smith@example.com',
              enrolledCourses: 5,
              completedCourses: 2,
              progress: 65,
              avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=ES',
              joinDate: '2024-01-15'
            },
            {
              id: 2,
              name: 'James Smith',
              age: 10,
              grade: '4th Grade',
              email: 'james.smith@example.com',
              enrolledCourses: 3,
              completedCourses: 1,
              progress: 45,
              avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=JS',
              joinDate: '2024-02-01'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching children:', error);
        // Use mock data as fallback
        setChildren([
          {
            id: 1,
            name: 'Emma Smith',
            age: 12,
            grade: '6th Grade',
            email: 'emma.smith@example.com',
            enrolledCourses: 5,
            completedCourses: 2,
            progress: 65,
            avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=ES',
            joinDate: '2024-01-15'
          },
          {
            id: 2,
            name: 'James Smith',
            age: 10,
            grade: '4th Grade',
            email: 'james.smith@example.com',
            enrolledCourses: 3,
            completedCourses: 1,
            progress: 45,
            avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=JS',
            joinDate: '2024-02-01'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, [parentId]);

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (!newChild.name || !newChild.email || !newChild.password) {
      alert(t('children.form.name') + ', ' + t('children.form.email') + ', and ' + t('children.form.password') + ' are required');
      return;
    }

    if (!parentId) {
      alert('Parent ID not found. Please log in again.');
      return;
    }

    try {
      const token = getAuthToken();
      const nameParts = newChild.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          parentId,
          firstName,
          lastName,
          email: newChild.email,
          password: newChild.password,
          age: newChild.age ? parseInt(newChild.age) : null,
          grade: newChild.grade || null,
          phone: null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh children list
        const childrenResponse = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (childrenResponse.ok) {
          const childrenData = await childrenResponse.json();
          if (childrenData.success) {
            setChildren(childrenData.data);
          }
        }

        setNewChild({ name: '', age: '', grade: '', email: '', password: '' });
        setShowAddChildModal(false);
        alert('Child added successfully!');
      } else {
        alert(data.message || 'Failed to add child');
      }
    } catch (error) {
      console.error('Error adding child:', error);
      alert('Failed to add child. Please try again.');
    }
  };

  const handleRemoveChild = async (childId) => {
    if (!window.confirm(t('children.confirmRemove'))) {
      return;
    }

    if (!parentId) {
      alert('Parent ID not found. Please log in again.');
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${childId}?parentId=${parentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setChildren(children.filter(child => child.id !== childId));
        alert('Child removed successfully!');
      } else {
        alert(data.message || 'Failed to remove child');
      }
    } catch (error) {
      console.error('Error removing child:', error);
      alert('Failed to remove child. Please try again.');
    }
  };

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <div>
          <h1 className="parent-content-title">{t('children.title')}</h1>
          <p className="parent-content-subtitle">{t('children.subtitle')}</p>
        </div>
        <button 
          className="parent-btn primary"
          onClick={() => setShowAddChildModal(true)}
        >
          <i className="material-icons">add</i>
          {t('children.addChild')}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="parent-loading">
          <p>{t('messages.loading', { ns: 'common' })}</p>
        </div>
      )}

      {/* Children List */}
      {!loading && (
        <div className="parent-section">
          {children.length === 0 ? (
          <div className="parent-empty-state">
            <i className="material-icons">people_outline</i>
            <p>{t('children.noChildren')}</p>
            <button 
              className="parent-btn primary"
              onClick={() => setShowAddChildModal(true)}
            >
              <i className="material-icons">add</i>
              {t('children.addFirstChild')}
            </button>
          </div>
        ) : (
          <div className="parent-children-table">
            <table>
              <thead>
                <tr>
                  <th>{t('children.table.name')}</th>
                  <th>{t('children.table.age')}</th>
                  <th>{t('children.table.grade')}</th>
                  <th>{t('children.table.courses')}</th>
                  <th>{t('children.table.progress')}</th>
                  <th>{t('children.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {children.map((child) => (
                  <tr key={child.id}>
                    <td>
                      <div className="parent-child-cell">
                        <img src={child.avatar} alt={child.name} className="parent-child-avatar-small" />
                        <div>
                          <strong>{child.name}</strong>
                          <p className="parent-child-email">{child.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>{child.age} {t('children.yearsOld')}</td>
                    <td>{child.grade}</td>
                    <td>
                      <span className="parent-badge">{child.enrolledCourses} {t('children.enrolled')}</span>
                    </td>
                    <td>
                      <div className="parent-progress-inline">
                        <div className="parent-progress-bar-small">
                          <div 
                            className="parent-progress-fill" 
                            style={{ width: `${child.progress}%` }}
                          ></div>
                        </div>
                        <span className="parent-progress-text">{child.progress}%</span>
                      </div>
                    </td>
                    <td>
                      <div className="parent-actions">
                        <button className="parent-btn-icon" title={t('children.viewProfile')}>
                          <i className="material-icons">visibility</i>
                        </button>
                        <button className="parent-btn-icon" title={t('children.edit')}>
                          <i className="material-icons">edit</i>
                        </button>
                        <button 
                          className="parent-btn-icon danger" 
                          title={t('children.remove')}
                          onClick={() => handleRemoveChild(child.id)}
                        >
                          <i className="material-icons">delete</i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* Add Child Modal */}
      {showAddChildModal && (
        <div className="parent-modal-overlay" onClick={() => setShowAddChildModal(false)}>
          <div className="parent-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="parent-modal-header">
              <h2>{t('children.addChild')}</h2>
              <button 
                className="parent-modal-close"
                onClick={() => setShowAddChildModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>

            <form onSubmit={handleAddChild} className="parent-form">
              <div className="parent-form-group">
                <label htmlFor="childName">{t('children.form.name')} *</label>
                <input
                  type="text"
                  id="childName"
                  value={newChild.name}
                  onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
                  required
                />
              </div>

              <div className="parent-form-group">
                <label htmlFor="childAge">{t('children.form.age')} *</label>
                <input
                  type="number"
                  id="childAge"
                  min="5"
                  max="18"
                  value={newChild.age}
                  onChange={(e) => setNewChild({ ...newChild, age: e.target.value })}
                  required
                />
              </div>

              <div className="parent-form-group">
                <label htmlFor="childGrade">{t('children.form.grade')} *</label>
                <input
                  type="text"
                  id="childGrade"
                  value={newChild.grade}
                  onChange={(e) => setNewChild({ ...newChild, grade: e.target.value })}
                  placeholder={t('children.form.gradePlaceholder')}
                  required
                />
              </div>

              <div className="parent-form-group">
                <label htmlFor="childEmail">{t('children.form.email')} *</label>
                <input
                  type="email"
                  id="childEmail"
                  value={newChild.email}
                  onChange={(e) => setNewChild({ ...newChild, email: e.target.value })}
                  required
                />
              </div>

              <div className="parent-form-group">
                <label htmlFor="childPassword">{t('children.form.password')} *</label>
                <input
                  type="password"
                  id="childPassword"
                  value={newChild.password}
                  onChange={(e) => setNewChild({ ...newChild, password: e.target.value })}
                  required
                />
              </div>

              <div className="parent-form-actions">
                <button type="submit" className="parent-btn primary">
                  <i className="material-icons">add</i>
                  {t('children.addChild')}
                </button>
                <button 
                  type="button" 
                  className="parent-btn secondary"
                  onClick={() => setShowAddChildModal(false)}
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentSectionChildren;

